/**
 * Declare an objective.
 *
 * This is the only way intent enters the system, and it exists because intent is
 * the one thing that cannot be distilled from correspondence. Everything else in
 * the vault is derived from what already happened; an objective is the owner
 * saying what he is aiming at, which no amount of reading his mail will reveal.
 *
 * A proposal never becomes an objective by itself. A proposal carries a
 * sentence-shaped title, and promoting that to an authored objective would fabricate
 * exactly the thing programme decision A5-3 says must be declared rather than
 * inferred. So the accept path is: he reads the proposal, and if he agrees he writes
 * the objective here, in his own words.
 *
 * What the form DOES accept from a proposal is a starting `summary` and a line of
 * provenance, because the proposal's sentence is a summary already. `name` stays
 * empty on purpose: the name becomes the filename and the identity of the objective,
 * so it has to be his. Before this, "Needs your judgement" offered Reject and a
 * disabled Accept, which is not a judgement, it is a nudge to reject everything.
 *
 * Keep it short. The vault's failure mode is sprawl, and an objective list long
 * enough to scroll is one nobody reads.
 *
 * ENGAGEMENTS ARE MATCHED BY NAME, and that is why this form does more than collect text.
 * `queries.ts` attributes work to an objective by comparing engagement names to note titles
 * and to a commitment's `topic`. A name that matches nothing attributes nothing, and the
 * objective then reports itself merely `objective-stalled`, which is indistinguishable from
 * real work that has gone quiet. A typo, a stale spelling, or the name of a merge tombstone
 * therefore produced an objective that looked declared and measured nothing, silently.
 *
 * So the field is checked against `/api/mo/engagements` as he types, using the SAME name
 * folding the server attributes with (`normalizeName` from `@rbv/shared`, which is shared for
 * exactly this reason). It stays a text box rather than becoming a widget, because pasting a
 * prepared list is the fastest way to declare a real objective and a chip-only control would
 * take that away.
 *
 * It WARNS and does not block. Objectives about work MO OS cannot see yet are legitimate and
 * expected: the brand lane lives in Hibiki and Postiz and has almost no vault footprint, so
 * refusing to declare an objective for it would be the tool overruling him about his own
 * intent. Naming what will not be counted is the honest middle.
 */

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { normalizeName, unresolvedEngagements, type EngagementCandidate } from '@rbv/shared';

export interface ObjectiveFormProps {
  /** Called after a successful write, so the dashboard can refetch. */
  onSaved: () => void;
  /** Optional cancel affordance, when the form is shown in a disclosure. */
  onCancel?: () => void;
  /**
   * A proposal's sentence, used to seed `summary` only. Never seeds `name`: see the
   * header note on why the name has to be authored.
   */
  initialSummary?: string;
  /** Shown above the fields so it is obvious which proposal is being answered. */
  answering?: string;
}

/**
 * One engagement per line. Empty entries are dropped rather than sent.
 *
 * LINES, NOT COMMAS, and this was not a style choice. 139 live topic and org names in this
 * vault contain a comma, including `Enterprise Intelligence, HUMAIN × Vantage`, which is the
 * single most important engagement for the objective about winning new Vantage clients. A
 * comma-separated field cannot express any of them: it would silently send two fragments,
 * both of which resolve to nothing, and the objective would measure nothing while looking
 * correctly declared.
 *
 * A pasted comma list still works. Any line that does not itself resolve to a known name is
 * split on commas as a fallback, so `A, B, C` pasted into an empty field becomes three names
 * while a real name containing a comma is left whole. With no candidates loaded there is
 * nothing to resolve against, so the fallback applies and the behaviour is what it always was.
 */
export function splitEngagements(raw: string, candidates: readonly EngagementCandidate[] = []): string[] {
  const known = new Set(candidates.map((c) => normalizeName(c.name)));
  const out: string[] = [];

  for (const line of raw.split('\n').map((l) => l.trim()).filter((l) => l !== '')) {
    if (!line.includes(',') || known.has(normalizeName(line))) {
      out.push(line);
      continue;
    }
    for (const part of line.split(',').map((x) => x.trim()).filter((x) => x !== '')) out.push(part);
  }

  return out;
}

/** How many suggestions to offer. Enough to recognise one, few enough to scan. */
const MAX_SUGGESTIONS = 8;

/*
 * Re-exported so this module's tests keep it as their subject. The implementation is in
 * `@rbv/shared` because the server attributes with the same one: a second copy in the browser
 * would drift, and the failure would be silent and in the worse direction, accepting a name
 * the deck never counts.
 */
export { unresolvedEngagements as unresolved };

/** The last line: what he is typing right now. */
export function activeToken(raw: string): string {
  const at = raw.lastIndexOf('\n');
  return (at === -1 ? raw : raw.slice(at + 1)).trim();
}

/** Replace the line being typed with a chosen name, leaving a fresh line for the next. */
export function completeWith(raw: string, name: string): string {
  const at = raw.lastIndexOf('\n');
  const head = at === -1 ? '' : raw.slice(0, at + 1);
  return `${head}${name}\n`;
}

export function ObjectiveForm({
  onSaved,
  onCancel,
  initialSummary,
  answering,
}: ObjectiveFormProps): JSX.Element {
  const [name, setName] = useState('');
  const [summary, setSummary] = useState(initialSummary ?? '');
  const [success, setSuccess] = useState('');
  const [horizon, setHorizon] = useState('');
  const [engagements, setEngagements] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [candidates, setCandidates] = useState<EngagementCandidate[]>([]);

  /*
   * Fetched once when the form opens rather than folded into the dashboard payload, which is
   * already 290KB and loads on every deck view. Failure is deliberately silent: an empty list
   * turns the checking off and leaves the plain text box, which is exactly how this worked
   * before. A form that refuses to open because a hint could not load would be worse.
   */
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const res = await fetch('/api/mo/engagements');
        if (!res.ok) return;
        const body = (await res.json()) as { engagements?: EngagementCandidate[] };
        if (live && Array.isArray(body.engagements)) setCandidates(body.engagements);
      } catch {
        // Offline, or A2 disabled. Both leave the field working as free text.
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const chosen = useMemo(() => splitEngagements(engagements, candidates), [engagements, candidates]);
  const missing = useMemo(() => unresolvedEngagements(chosen, candidates), [chosen, candidates]);

  /** Names matching what he is typing, minus the ones already in the field. */
  const suggestions = useMemo(() => {
    const token = normalizeName(activeToken(engagements));
    if (token === '' || candidates.length === 0) return [];
    const taken = new Set(chosen.map((c) => normalizeName(c)));
    return candidates
      .filter((c) => !taken.has(normalizeName(c.name)) && normalizeName(c.name).includes(token))
      .slice(0, MAX_SUGGESTIONS);
  }, [engagements, candidates, chosen]);

  /** Engagements already counted toward another objective. First claim wins, server-side. */
  const conflicts = useMemo(() => {
    const taken = new Set(chosen.map((c) => normalizeName(c)));
    return candidates.filter((c) => taken.has(normalizeName(c.name)) && c.claimedBy !== '');
  }, [chosen, candidates]);

  const submit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const trimmed = name.trim();
      // Checked here as well as on the server: the name becomes the filename, and a
      // round trip to be told so is a worse experience than not sending.
      if (trimmed === '') {
        setError('An objective needs a name.');
        return;
      }

      setSaving(true);
      setError('');
      void (async () => {
        try {
          const res = await fetch('/api/mo/objectives', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              name: trimmed,
              summary: summary.trim(),
              success: success.trim(),
              horizon: horizon.trim(),
              engagements: splitEngagements(engagements, candidates),
            }),
          });

          if (!res.ok) {
            // Prefer the server's own wording: it knows why it refused.
            let detail = `save failed (${res.status})`;
            try {
              const body = (await res.json()) as { error?: string };
              if (typeof body.error === 'string' && body.error !== '') detail = body.error;
            } catch {
              // Non-JSON error body; the status is all we have.
            }
            setError(detail);
            return;
          }

          // Cleared only on success, so a failure never costs him what he typed.
          setName('');
          setSummary('');
          setSuccess('');
          setHorizon('');
          setEngagements('');
          onSaved();
        } catch (err) {
          setError(`save failed: ${(err as Error).message}`);
        } finally {
          setSaving(false);
        }
      })();
    },
    [name, summary, success, horizon, engagements, candidates, onSaved],
  );

  return (
    <form className="mo-objective-form" onSubmit={submit}>
      <h3>{answering === undefined ? 'Declare an objective' : 'Accept as an objective'}</h3>
      {answering !== undefined && (
        <p className="mo-form-answering">
          Answering: <em>{answering}</em>
        </p>
      )}
      <p className="mo-form-hint">
        What you are aiming at. This cannot be read out of your mail, so it is the one thing you have to
        say yourself. Keep the list short.
      </p>

      <label>
        <span>Name</span>
        <input name="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Win AstraZeneca Medical Affairs" />
      </label>

      <label>
        <span>What it is</span>
        <textarea name="summary" value={summary} onChange={(e) => setSummary(e.target.value)} rows={2} />
      </label>

      <label>
        <span>What good looks like</span>
        <textarea name="success" value={success} onChange={(e) => setSuccess(e.target.value)} rows={2} />
      </label>

      <label>
        <span>Horizon</span>
        <input name="horizon" value={horizon} onChange={(e) => setHorizon(e.target.value)} placeholder="Q4 2026" />
      </label>

      <label>
        <span>Engagements it is served by</span>
        <textarea
          name="engagements"
          value={engagements}
          onChange={(e) => setEngagements(e.target.value)}
          rows={4}
          placeholder={'MDT360\nRosetta'}
          autoComplete="off"
        />
        <small>
          One per line. These are what activity gets measured against, matched by name.
        </small>
      </label>

      {suggestions.length > 0 && (
        <ul className="mo-engagement-suggest">
          {suggestions.map((c) => (
            <li key={`${c.type}:${c.name}`}>
              <button type="button" onClick={() => setEngagements(completeWith(engagements, c.name))}>
                <span className="mo-eng-name">{c.name}</span>
                <span className="mo-eng-meta">
                  {c.type}
                  {c.status === 'dormant' ? ' - dormant' : ''} - last seen {c.lastSeen}
                  {c.claimedBy === '' ? '' : ` - already serving ${c.claimedBy}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {missing.length > 0 && (
        <p className="mo-form-warn">
          {missing.length === 1 ? 'This name matches nothing' : 'These names match nothing'} in the
          vault, so no activity will be attributed through{' '}
          {missing.length === 1 ? 'it' : 'them'}: <strong>{missing.join(', ')}</strong>. You can still
          declare it, and the objective will read as stalled until something by that name exists.
        </p>
      )}

      {conflicts.length > 0 && (
        <p className="mo-form-warn">
          {conflicts.map((c) => `${c.name} already serves ${c.claimedBy}`).join('; ')}. An engagement
          counts for one objective only, whichever claimed it first.
        </p>
      )}

      {error !== '' ? <p className="mo-form-error">{error}</p> : null}

      <div className="mo-form-actions">
        <button type="submit" className="mo-primary" disabled={saving}>
          {saving ? 'Saving' : 'Declare'}
        </button>
        {onCancel ? (
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}
